import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  CATEGORIES_COLLECTION,
  CUSTOMER_COLLECTION,
  INVENTORY_COLLECTION,
  INVOICES_COLLECTION,
  ROUTES_COLLECTION,
  TABLE_INVOICES_COLLECTION,
  TRUCKS_COLLECTION,
  USERS_COLLECTION,
} from "../utils/models/collections/collectionConstants";
import { checkInvoices } from "../utils/models/checkers/checkInvoices";
import moment from "moment";
import {
  INVOICE,
  RECEIVE_METHOD_DELIVERY,
  RETURN_METHOD_ONSITE,
} from "../utils/models/modelConstants/modelConstants";
import { ConfigureStopFromInvoice } from "../utils/models/configure/configureStopFromInvoice";
import { CheckRoute } from "../utils/models/checkers/checkRoute";
import { CheckTruck } from "../utils/models/checkers/checkTruck";
import checkUser from "../utils/models/checkers/checkUser";
import { firstNameSorter, sortByValue } from "../utils/sorters/firstNameSorter";
import { invoiceUtcOffsetQuery } from "../utils/models/configure/wrapper/invoiceUtcOffsetQuery";
import { checkCategory } from "../utils/models/checkers/checkCategory";
import { checkInventory } from "../utils/models/checkers/checkInventory";
import { checkCustomer } from "../utils/models/checkers/checkCustomer";
import { checkTableRowInvoices } from "../utils/models/checkers/checkTableRowInvoice";
export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({ baseUrl: "/" }),
  endpoints: (builder) => ({
    
    /**
     * MARK: Invoices
     */
    getInvoicesForDay: builder.query({
      async queryFn(params) {
        const { orgPrefix, moment: date, orgTimezone } = params;
        const { firestore } = params.db;
        const localStart = new moment(date).startOf("day").toDate();
        const localEnd = new moment(date).endOf("day").toDate();
        const convertedOrgTimes = invoiceUtcOffsetQuery().forDates({
          startDate: localStart,
          endDate: localEnd,
          orgTimezone,
        });
        const start = convertedOrgTimes.start;
        const end = convertedOrgTimes.end;
        try {
          const startRef = firestore
            .collection(`${orgPrefix}${INVOICES_COLLECTION}`)
            .where("rentalDateStart", ">=", start)
            .where("rentalDateStart", "<=", end);
          const startSnap = await startRef.get();
          let startList = [];
          startSnap.forEach((s) => {
            const i = checkInvoices(s, orgTimezone);
            if (i?.receiveMethod === RECEIVE_METHOD_DELIVERY) {
              startList.push(
                ConfigureStopFromInvoice({
                  invoice: checkInvoices(s, orgTimezone),
                  stopType: "receiveMethod",
                })
              );
            }
          });
          const endRef = firestore
            .collection(`${orgPrefix}${INVOICES_COLLECTION}`)
            .where("rentalDateEnd", ">=", start)
            .where("rentalDateEnd", "<=", end);
          const endSnap = await endRef.get();
          let endList = [];
          endSnap.forEach((s) => {
            const i = checkInvoices(s, orgTimezone);
            if (i?.returnMethod === RETURN_METHOD_ONSITE) {
              endList.push(
                ConfigureStopFromInvoice({
                  invoice: checkInvoices(s, orgTimezone),
                  stopType: "returnMethod",
                })
              );
            }
          });
          const fullData = startList.concat(endList).flat();
          const data = fullData.filter((s) => s?.invoice?.type === "invoice");
          return {
            data,
          };
        } catch (err) {
          return { error: err };
        }
      },
    }),
    getInvoicesForRange: builder.query({
      async queryFn(params) {
        const { orgPrefix, dateStart, dateEnd, orgTimezone, invoiceOnly } =
          params;
        const { firestore } = params.db;
        const localStart = new moment(dateStart).startOf("day").toDate();
        const localEnd = new moment(dateEnd).endOf("day").toDate();
        const convertedOrgTimes = invoiceUtcOffsetQuery().forDates({
          startDate: localStart,
          endDate: localEnd,
          orgTimezone,
        });
        const start = convertedOrgTimes.start;
        const end = convertedOrgTimes.end;
        try {
          const ref = firestore
            .collection(`${orgPrefix}${INVOICES_COLLECTION}`)
            .where("rentalDateStart", ">=", start)
            .where("rentalDateStart", "<=", end);
          const snap = await ref.get();
          let data = [];
          snap.forEach((s) => {
            const i = checkInvoices(s, orgTimezone);
            if (invoiceOnly) {
              if (i.type === INVOICE) {
                data.push(i);
              }
            } else {
              data.push(i);
            }
          });
          return { data };
        } catch (err) {
          return { error: err };
        }
      },
    }),
    getTableRowInvoicesForRange: builder.query({
      async queryFn(params) {
        const { orgPrefix, dateStart, dateEnd, orgTimezone } = params;
        const { firestore } = params.db;

        const localStart = new moment(dateStart).startOf("day").toDate();
        const localEnd = new moment(dateEnd).endOf("day").toDate();

        const convertedOrgTimes = invoiceUtcOffsetQuery().forDates({
          startDate: localStart,
          endDate: localEnd,
          orgTimezone,
        });
        const start = convertedOrgTimes.start;
        const end = convertedOrgTimes.end;
        try {
          const querySnapshot = await firestore
            .collection(`${orgPrefix}${TABLE_INVOICES_COLLECTION}`)
            .where("rentalDateStart", ">=", start)
            .where("rentalDateStart", "<=", end)
            .get();

          let data = querySnapshot.docs.map((s) => {
            return checkTableRowInvoices(s, orgTimezone);
          }).filter(item => item !== null);

          return { data };
        } catch (err) {
          return { error: err };
        }
      },
    }),
    getInvoicesForRangeIntersect: builder.query({
      async queryFn(params) {
        const {
          orgPrefix,
          dateStart,
          dateEnd,
          orgTimezone,
          useTurnaround,
          invoiceOnly,
        } = params;
        const { firestore } = params.db;
        const localStart = new moment(dateStart).startOf("day").toDate();
        const localEnd = new moment(dateEnd).endOf("day").toDate();
        const convertedOrgTimes = invoiceUtcOffsetQuery().forDates({
          startDate: localStart,
          endDate: localEnd,
          orgTimezone,
        });
        const start = convertedOrgTimes.start;
        try {
          const ref = firestore
            .collection(`${orgPrefix}${INVOICES_COLLECTION}`)
            .where("rentalDateEnd", ">", start);
          const snap = await ref.get();
          let list = [];
          snap.forEach((s) => {
            const i = checkInvoices(s, orgTimezone);
            if (invoiceOnly) {
              if (i.type === INVOICE) {
                list.push(i);
              }
            } else {
              list.push(i);
            }
          });
          const filtered = list?.filter((i) => {
            const start = new Date(i.rentalDateStart.seconds * 1000);
            return start < localEnd;
          });
          return { data: filtered };
        } catch (err) {
          return { error: err };
        }
      },
    }),
    //MARK: routes
    getRoutesForMonth: builder.query({
      async queryFn(params) {
        const { orgPrefix, monthStart, monthEnd, orgTimezone } = params;
        const { firestore } = params.db;
        const localStart = new moment(monthStart).startOf("day").toDate();
        const localEnd = new moment(monthEnd).endOf("day").toDate();
        const convertedOrgTimes = invoiceUtcOffsetQuery().forDates({
          startDate: localStart,
          endDate: localEnd,
          orgTimezone,
        });

        const start = convertedOrgTimes.start;
        const end = convertedOrgTimes.end;

        try {
          const ref = firestore
            .collection(`${orgPrefix}${ROUTES_COLLECTION}`)
            .where("routeDate", ">=", start)
            .where("routeDate", "<=", end)
            
          const snapshotQuery = await ref.get();

          let routes = [];
          snapshotQuery.docs.forEach((s) => {  
            const i = CheckRoute(s, orgTimezone);
            routes.push(i);
          });
          const sortedRoutes = [...routes].sort((a, b) => a.listIndex - b.listIndex);

          return { data: sortedRoutes };
        } catch (err) {
          return { error: err };
        }
      },  
    }),
    getRoutesForDay: builder.query({
      async queryFn(params) {
        const { orgPrefix, date, orgTimezone } = params;
        const { firestore } = params.db;
        const localStart = new moment(date).startOf("day").toDate();
        const localEnd = new moment(date).endOf("day").toDate();
        const convertedOrgTimes = invoiceUtcOffsetQuery().forDates({
          startDate: localStart,
          endDate: localEnd,
          orgTimezone,
        });
        const start = convertedOrgTimes.start;
        const end = convertedOrgTimes.end;
        try {
          const ref = firestore
            .collection(`${orgPrefix}${ROUTES_COLLECTION}`)
            .where("routeDate", ">=", start)
            .where("routeDate", "<=", end);
          const snap = await ref.get();
          let routes = [];
          snap.forEach((s) => {
            const i = CheckRoute(s, orgTimezone);
            routes.push(i);
          });
          const sortedRoutes = [...routes].sort((a, b) => a.listIndex - b.listIndex);
          return { data: sortedRoutes };
        } catch (err) {
          return { error: err };
        }
      },
    }),
    //MARK: trucks
    getTrucks: builder.query({
      async queryFn(params) {
        const { orgPrefix } = params;
        const { firestore } = params.db;

        try {
          const ref = firestore.collection(`${orgPrefix}${TRUCKS_COLLECTION}`);
          const snap = await ref.get();
          let data = [];
          snap.forEach((s) => {
            const i = CheckTruck(s);
            data.push(i);
          });
          return { data };
        } catch (err) {
          return { error: err };
        }
      },
    }),
    //MARK:  users
    getUsers: builder.query({
      async queryFn(params) {
        const { orgPrefix } = params;
        const { firestore } = params.db;

        try {
          const ref = firestore.collection(`${orgPrefix}${USERS_COLLECTION}`);
          const snap = await ref.get();
          let data = [];
          snap.forEach((s) => {
            const i = checkUser(s);
            data.push(i);
          });
          return { data: firstNameSorter(data) };
        } catch (err) {
          return { error: err };
        }
      },
    }),
   //MARK: categories
    getCategories: builder.query({
      async queryFn(params) {
        const { orgPrefix } = params;
        const { firestore } = params.db;

        try {
          const ref = firestore.collection(
            `${orgPrefix}${CATEGORIES_COLLECTION}`
          );
          const snap = await ref.get();
          let data = [];
          snap.forEach((s) => {
            const i = checkCategory(s);
            data.push(i);
          });
          return { data: sortByValue(data, "name") };
        } catch (err) {
          return { error: err };
        }
      },
    }),
    //MARK: inventory
    getInventory: builder.query({
      async queryFn(params) {
        const { orgPrefix } = params;
        const { firestore } = params.db;

        try {
          const ref = firestore.collection(
            `${orgPrefix}${INVENTORY_COLLECTION}`
          );
          const snap = await ref.get();
          let data = [];
          snap.forEach((s) => {
            const i = checkInventory(s);
            data.push(i);
          });
          return { data: sortByValue(data, "name") };
        } catch (err) {
          return { error: err };
        }
      },
    }),
    //MARK: customers
    getInitialPaginatedCustomers: builder.query({
      async queryFn(params) {
        const { orgPrefix, recordsPerFetch, orderBy } = params;
        const { firestore } = params.db;

        const limit = recordsPerFetch ? recordsPerFetch : 50;
        try {
          const ref = firestore
            .collection(`${orgPrefix}${CUSTOMER_COLLECTION}`)
            .orderBy(orderBy)
            .limit(limit);

          const snap = await ref.get();
          let data = [];
          snap.forEach((s) => {
            const i = checkCustomer(s);
            data.push(i);
          });
          return {
            data: {
              docs: [...data],
              lastVisible: snap.docs[snap.docs.length - 1],
            },
          };
        } catch (err) {
          console.log(err);
          return {
            data: {
              docs: [],
              lastVisible: null,
            },
          };
        }
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetInvoicesForDayQuery,
  useGetInvoicesForRangeQuery,
  useGetTableRowInvoicesForRangeQuery,
  useGetRoutesForMonthQuery,
  useGetTrucksQuery,
  useGetRoutesForDayQuery,
  useGetUsersQuery,
  useGetCategoriesQuery,
  useGetInventoryQuery,
  useGetInvoicesForRangeIntersectQuery,
  useGetInitialPaginatedCustomersQuery,
  useLazyGetInitialPaginatedCustomersQuery,
} = api;
